/**
 * Session lifecycle.
 *
 * Supabase Auth owns the tokens; this wires them into the API client and exposes
 * them to React. A failed refresh signs out cleanly with `?next=` preserved, so
 * the user returns to where they were.
 */

import type { Session, User } from '@supabase/auth-js'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { setTokenProvider } from '../api/client'
import { auth, disconnectRealtime } from '../supabase/client'

let currentAccessToken: string | null = null

export function getEffectiveAccessToken(): string | null {
  if (typeof window !== 'undefined') {
    try {
      const impersonationToken = sessionStorage.getItem('convo_impersonation_token')
      if (impersonationToken) return impersonationToken
    } catch {
      // In private browsing or sandboxed iframes
    }
  }
  return currentAccessToken
}

// The API client asks for the token synchronously on every request.
setTokenProvider(getEffectiveAccessToken)

export interface AuthState {
  session: Session | null
  user: User | null
  status: 'loading' | 'authenticated' | 'anonymous'
}

export function useSession(): AuthState {
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    status: 'loading',
  })

  useEffect(() => {
    let active = true

    void auth.getSession().then(({ data }) => {
      if (!active) return
      currentAccessToken = data.session?.access_token ?? null
      setState({
        session: data.session,
        user: data.session?.user ?? null,
        status: data.session ? 'authenticated' : 'anonymous',
      })
    })

    const { data: subscription } = auth.onAuthStateChange((_event, session) => {
      currentAccessToken = session?.access_token ?? null
      setState({
        session,
        user: session?.user ?? null,
        status: session ? 'authenticated' : 'anonymous',
      })
    })

    return () => {
      active = false
      subscription.subscription.unsubscribe()
    }
  }, [])

  return state
}

export function useSignOut() {
  const queryClient = useQueryClient()

  return useCallback(
    async (scope: 'local' | 'global' = 'local') => {
      // Clear the cache BEFORE signing out: a cached row must never be
      // renderable once the session is gone.
      queryClient.clear()
      disconnectRealtime()
      currentAccessToken = null
      await auth.signOut({ scope })
    },
    [queryClient],
  )
}

export function getAccessToken(): string | null {
  return currentAccessToken
}

export interface ImpersonationSessionData {
  token: string
  expiresAt: string
  workspaceSlug: string
}

export function startImpersonation(token: string, expiresAt: string, workspaceSlug: string): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem('convo_impersonation_token', token)
    sessionStorage.setItem(
      'convo_impersonation_info',
      JSON.stringify({ token, expiresAt, workspaceSlug }),
    )
    window.dispatchEvent(new Event('convo_impersonation_change'))
  } catch {
    // ignore sessionStorage errors
  }
}

export function stopImpersonation(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem('convo_impersonation_token')
    sessionStorage.removeItem('convo_impersonation_info')
    window.dispatchEvent(new Event('convo_impersonation_change'))
  } catch {
    // ignore sessionStorage errors
  }
}

export function getImpersonationSession(): ImpersonationSessionData | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem('convo_impersonation_info')
    if (!raw) return null
    return JSON.parse(raw) as ImpersonationSessionData
  } catch {
    return null
  }
}

export function useImpersonation(): ImpersonationSessionData | null {
  const [data, setData] = useState<ImpersonationSessionData | null>(getImpersonationSession)

  useEffect(() => {
    function onSync() {
      setData(getImpersonationSession())
    }
    window.addEventListener('convo_impersonation_change', onSync)
    window.addEventListener('storage', onSync)
    return () => {
      window.removeEventListener('convo_impersonation_change', onSync)
      window.removeEventListener('storage', onSync)
    }
  }, [])

  return data
}
